/*
 * Description:
 * ---------------------
 * Data Budgets: how much each internet line has carried this month against
 * the budget its plan allows, and how much the box may use the line
 * (cloud-anchor/pkg/posture).
 *
 * A line is in use, kept only as a backup (it carries traffic when the other
 * lines are down, and never joins them to add speed), or off (nothing at all
 * leaves by it). The operator chooses one, and a budget can make it stricter
 * as the month's usage climbs: an alert, then backup only, then off until the
 * month starts again; the box's last line on is slowed instead of switched
 * off, and the screen says so in an alarm. What the screen shows is the
 * appliance's own count and decision, never what was asked. All wording lives
 * in budgetWords.js.
 *
 * ! file components/DataBudgetsPanel.js
 */
import React, { useState } from "react";
import { FiAlertOctagon, FiAlertTriangle, FiEdit2 } from "react-icons/fi";
import useEdgeFeature, { FEATURE_ABSENT, FEATURE_PRESENT, FEATURE_UNREACHABLE } from "../hooks/useEdgeFeature";
import useEdgeWrite from "../hooks/useEdgeWrite";
import useVisibleInterval from "../hooks/useVisibleInterval";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import { useConfirm } from "./ConfirmModal";
import { useWanName, WanLabel } from "./WanNames";
import * as W from "./budgetWords";
import "./DeviceLimits.css";
import "./DataBudgets.css";

/* What switching a line off cuts, wherever it can be done. */
function OffWarning() {
  return <p className="db-warning" role="note"><FiAlertTriangle size={15} /> <span>{W.OFF_MEANS}</span></p>;
}

/* What a budget does to the box's last line on, wherever one can run out. */
function SlowWarning({ rate }) {
  return <p className="db-warning db-danger" role="note"><FiAlertOctagon size={15} /> <span>{W.slowMeans(rate)}</span></p>;
}

/* The usage past which a budget switches the line off (or slows the last line
   on), 0 when it never does. */
function offAt(setting) {
  return setting?.budget ? W.rungBytes(setting.budget, "cold_pct") : 0;
}

/* Whether saving setting acts at once: the line has already used past it. */
function actsNow(line, setting) {
  const at = offAt(setting);
  return at > 0 && line.usage.bytes >= at && line.posture !== "cold" && !line.throttled;
}

/* How full the month is, with each rung marked where it takes effect. */
function UsageBar({ line }) {
  const b = line.setting.budget;
  const marks = W.RUNGS.map(([key, label]) => [label, W.rungBytes(b, key)]).filter(([, v]) => v > 0);
  const top = Math.max(b.limit_bytes, line.usage.bytes, ...marks.map(([, v]) => v));
  const at = (v) => `${Math.min(100, (100 * v) / top)}%`;
  return (
    <div className="db-bar" role="img" aria-label={`${W.usedPct(line)}% of the budget used`}>
      <div className={`db-fill db-${W.stateChip(line).tone}`} style={{ width: at(line.usage.bytes) }} />
      {marks.map(([label, v]) => (
        <span key={label} className="db-mark" style={{ left: at(v) }} title={`${label} ${W.gb(v)}`} />
      ))}
    </div>
  );
}

function LineCard({ line, slowsTo, rate, onEdit }) {
  const wanName = useWanName();
  const chip = W.stateChip(line);
  const why = W.why(line, rate);
  const next = W.nextWords(line, slowsTo);
  return (
    <div className="panel db-line">
      <div className="db-line-head">
        <strong><WanLabel iface={line.name} /></strong>
        <span className={`db-chip db-${chip.tone}`}>{chip.label}</span>
        {!line.present && <span className="dl-muted">Not connected now</span>}
        <button type="button" className="dl-btn icon db-edit" aria-label={`Edit ${wanName(line.name)}`} onClick={onEdit}>
          <FiEdit2 size={14} />
        </button>
      </div>
      {why && <p className="db-why">{why}</p>}
      <p className="db-usage">{W.usageWords(line)}</p>
      {line.setting?.budget && <UsageBar line={line} />}
      {next && <p className="dl-note">{next}</p>}
      <details className="dl-measure">
        <summary>Show the measurements</summary>
        {W.measureWords(line).map((s) => <span key={s}>{s}</span>)}
      </details>
    </div>
  );
}

function LineForm({ line, slowsTo, maxDay, busy, onSave, onCancel }) {
  const [form, setForm] = useState(() => W.formFromSetting(line.setting));
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const budgeted = String(form.limit_gb).trim() !== "";
  const asked = W.settingFromForm(form);
  const submit = (e) => {
    e.preventDefault();
    onSave(W.settingFromForm(form));
  };
  return (
    <form className="panel dl-form db-form" onSubmit={submit}>
      <strong><WanLabel iface={line.name} /></strong>
      <label className="dl-field">
        <span>Use this line</span>
        <select className="dl-input" value={form.posture} onChange={set("posture")} aria-label="Use this line">
          {W.USE_CHOICES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      {form.posture === "standby" && <p className="dl-note">{W.BACKUP_MEANS}</p>}
      {form.posture === "cold" && <OffWarning />}
      <div className="dl-fields">
        <label className="dl-field">
          <span>Monthly budget</span>
          <input className="dl-input" type="number" min="0.1" step="any" inputMode="decimal" placeholder="None"
            value={form.limit_gb} onChange={set("limit_gb")} aria-label="Monthly budget in GB" />
          <span className="dl-unit">GB</span>
        </label>
        <label className="dl-field">
          <span>The month starts on day</span>
          <select className="dl-input" value={form.cycle_day} onChange={set("cycle_day")} aria-label="Day the month starts">
            {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
      </div>
      {budgeted && (
        <fieldset className="dl-lines">
          <legend>As the month's usage climbs</legend>
          {W.RUNGS.map(([key, label, means]) => (
            <label key={key} className="dl-field db-rung">
              <span className="db-rung-name">{label}</span>
              <input className="dl-input" type="number" min="1" max="200" placeholder="Never"
                value={form[key]} onChange={set(key)} aria-label={`${label} percent of the budget`} />
              <span className="dl-unit">%</span>
              <span className="db-means">{means}</span>
            </label>
          ))}
          {form.posture !== "cold" && String(form.cold_pct).trim() !== "" && (slowsTo ? <SlowWarning rate={slowsTo} /> : <OffWarning />)}
        </fieldset>
      )}
      {actsNow(line, asked) && (
        <p className="db-warning db-danger" role="alert">
          <FiAlertOctagon size={15} /> <span>{W.actsNowWords(line, offAt(asked), slowsTo)}</span>
        </p>
      )}
      <div className="dl-actions">
        <button type="submit" className="dl-btn primary" disabled={busy}>Save</button>
        <button type="button" className="dl-btn" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

export default function DataBudgetsPanel({ selectedNode }) {
  const { state, payload, refresh } = useEdgeFeature(selectedNode, "/wans/posture");
  const { busy, error, send } = useEdgeWrite(selectedNode, refresh);
  const confirm = useConfirm();
  const wanName = useWanName();
  const [editing, setEditing] = useState(null);
  const view = state === FEATURE_PRESENT ? payload : null;
  useVisibleInterval(refresh, 15000, !!view?.available);

  if (!selectedNode) {
    return <div className="panel"><div className="panel-sub">Select a node to see its lines' data budgets.</div></div>;
  }
  if (state === FEATURE_ABSENT) {
    return (
      <FeatureUnavailableNotice nodeId={selectedNode} feature="Data budgets"
        detail="This appliance is running a build from before data budgets shipped. Once it is
                updated, this screen fills in on its own, and nothing else on the box is affected." />
    );
  }
  if (state === FEATURE_UNREACHABLE) {
    return (
      <div className="panel dl-warn">
        <div className="panel-title"><FiAlertTriangle size={17} /> Could not read data budgets from {selectedNode}</div>
        <div className="panel-sub">The request did not come back. The screen keeps trying and fills in on its own.</div>
      </div>
    );
  }
  if (!view) {
    return <div className="panel"><div className="panel-sub">Reading data budgets…</div></div>;
  }
  if (!view.available) {
    return (
      <div className="panel dl-warn">
        <div className="panel-title"><FiAlertTriangle size={17} /> Data budgets are off on this appliance</div>
        <div className="panel-sub">{view.reason}</div>
      </div>
    );
  }

  const lines = view.lines || [];
  const rate = W.rateWords(view.throttle_rate);
  const slowsTo = (line) => (rate && W.lastLineOn(lines, line) ? rate : "");
  const head = W.headline(lines, wanName, rate);
  const save = async (line, setting) => {
    const name = wanName(line.name);
    let ask = null;
    if (setting.posture === "cold" && line.setting?.posture !== "cold") {
      ask = { title: `Switch ${name} off?`, message: W.OFF_MEANS, confirmLabel: "Switch it off" };
    } else if (actsNow(line, setting)) {
      ask = slowsTo(line)
        ? { title: `Slow ${name} to ${slowsTo(line)} now?`, confirmLabel: "Slow it down" }
        : { title: `Switch ${name} off now?`, confirmLabel: "Switch it off" };
      ask.message = W.actsNowWords(line, offAt(setting), slowsTo(line));
    }
    if (ask && !(await confirm({ ...ask, variant: "danger" }))) return;
    if (await send("PUT", "/wans/posture", { name: line.name, ...setting })) setEditing(null);
  };

  return (
    <div className="dl db">
      {error && <div className="dl-error" role="alert">{error}</div>}
      {lines.filter((l) => l.present && l.throttled).map((l) => {
        const alarm = W.slowedAlarm(l, rate, wanName);
        return (
          <div key={l.name} className="db-alarm" role="alert">
            <FiAlertOctagon size={26} />
            <div><strong>{alarm.title}</strong><p>{alarm.detail}</p></div>
          </div>
        );
      })}
      <div className={`panel db-headline db-${head.tone}`}>{head.text}</div>
      {lines.length === 0 && <div className="panel"><div className="panel-sub">This box has no internet line right now.</div></div>}
      {lines.map((line) => (editing === line.name ? (
        <LineForm key={line.name} line={line} slowsTo={slowsTo(line)} maxDay={view.max_cycle_day || 28} busy={busy}
          onSave={(s) => save(line, s)} onCancel={() => setEditing(null)} />
      ) : (
        <LineCard key={line.name} line={line} slowsTo={slowsTo(line)} rate={rate} onEdit={() => setEditing(line.name)} />
      )))}
      <p className="dl-note">{W.COUNTS_WHAT}</p>
    </div>
  );
}
